import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const capDAilEn: ChauffeurPage = {
  slug: "cap-d-ail",
  title: "VTC & private chauffeur in Cap-d’Ail",
  subtitle:
    "Private chauffeur in Cap-d’Ail: Monaco on the doorstep, Mala beach, the coastal path, the station and a ~30 min transfer to Nice airport.",
  keywords: [
    "vtc cap-d’ail",
    "private chauffeur cap-d’ail",
    "cap-d-ail monaco chauffeur",
    "cap-d’ail nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Cap-d’Ail",
    subtitle:
      "The French town on Monaco’s doorstep: transfers into the Principality, Mala beach and the coastal path, with a dedicated chauffeur.",
    keywords: [
      "VTC Cap-d’Ail",
      "Monaco chauffeur",
      "Nice airport transfer",
      "Mala beach chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Cap-d’Ail",
    badges: ["Monaco doorstep", "Mala beach", "~30 min from the airport"],
  },
  benefits: {
    heading: "Why a chauffeur in Cap-d’Ail",
    items: [
      {
        title: "On Monaco’s doorstep",
        description:
          "The last French town before the Principality: reach Fontvieille, Monte-Carlo and the Rock without facing Monaco’s traffic and parking.",
      },
      {
        title: "Mala beach & coves",
        description:
          "Mala beach is reached down long stairways: the chauffeur drops you at the top and collects you at the same spot, with no parking detour.",
      },
      {
        title: "Coastal path & station",
        description:
          "From the Pointe des Douaniers to Cap-d’Ail station: handy meeting points for the shoreline walk as well as the train.",
      },
      {
        title: "~30 minutes from the airport",
        description:
          "A direct transfer to and from Nice airport, via the lower corniche or the A8, with flight tracking.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Cap-d’Ail",
    services: [
      {
        title: "Transfers into Monaco",
        text: "Business meetings, the casino, restaurants and nights out in Monte-Carlo: drop-off as close as possible, with no parking to chase in the Principality.",
      },
      {
        title: "Nice airport transfers",
        text: "Cap-d’Ail ⇄ Nice airport in about 30 minutes, with flight tracking and a welcome on arrival.",
      },
      {
        title: "Mala beach & the shore",
        text: "Drop-off at the top of the Mala beach stairways and along the shoreline, with pick-up on request.",
      },
      {
        title: "Monaco events",
        text: "Grand Prix, Yacht Show and galas: shuttles from Cap-d’Ail to skirt the road closures and the crowds of the Principality.",
      },
      {
        title: "Hourly hire & corniches",
        text: "By the hour or by the day to link Monaco, Èze, Beausoleil and Nice along the corniches.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Mala beach",
        description:
          "A preserved cove reached by stairways from the upper town.",
      },
      {
        name: "Coastal path",
        description:
          "The shoreline walk from the Pointe des Douaniers, around the cape.",
      },
      {
        name: "Cap-d’Ail station",
        description: "A rail halt a step from Monaco, on the coastal line.",
      },
      {
        name: "Monaco border",
        description:
          "Access to Fontvieille and Monte-Carlo, at the edge of the Principality.",
      },
      {
        name: "Château des Terrasses",
        description: "An estate and its gardens overlooking the sea.",
      },
      {
        name: "Jardin des Oliviers",
        description: "A seafront park on the path towards Monaco.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur on the Principality’s doorstep",
    subheading:
      "Cap-d’Ail lives to Monaco’s rhythm while keeping its coves and its coastal path.",
    paragraphs: [
      "Backing onto the Principality, Cap-d’Ail is the French gateway to Monaco: a few minutes are enough to reach Fontvieille or Monte-Carlo. Rather than tackle Monaco’s traffic and car parks, a chauffeur drops you as close as possible and stays reachable for the pick-up.",
      "KS Rent Services works with fixed fares set in advance — including Cap-d’Ail ⇄ Nice airport — and hourly or daily hire, with an immediate quote. Our bilingual French/English chauffeurs, based on the French Riviera, are rated 5.0/5 from 38 Google reviews.",
      "Between Mala beach tucked into its cove, the coastal path around the cape and the nearby station, Cap-d’Ail blends seaside life with immediate closeness to Monaco: the chauffeur links these points with parking never entering the picture.",
    ],
    bullets: [
      "Transfers into Monaco with no parking in the Principality",
      "Fixed fares & hourly hire, immediate quote",
      "Bilingual FR/EN chauffeurs, 5.0/5 from 38 Google reviews",
    ],
  },
  process: {
    heading: "Book a chauffeur in Cap-d’Ail",
    steps: [
      "Share your programme: Monaco, Mala beach, airport transfer or an event.",
      "We confirm the vehicle and the fixed fare or the hourly hire.",
      "The chauffeur picks you up at your villa, hotel or the station.",
      "Live adjustments to the traffic and the closures around Monaco.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Cap-d’Ail"),
    {
      q: "How long does it take to reach Nice airport?",
      a: "About 30 minutes via the lower corniche or the A8. The chauffeur tracks your flight and adjusts the departure time for traffic on the way into Nice.",
    },
    {
      q: "Do you handle transfers into Monaco from Cap-d’Ail?",
      a: "Yes. We take you to Fontvieille, Monte-Carlo or the Rock and drop you as close as possible, with no need to deal with parking in the Principality.",
    },
    {
      q: "Do you operate during the Monaco Grand Prix?",
      a: "Yes. During the Grand Prix, the Yacht Show and galas, we run shuttles from Cap-d’Ail to work around the street closures and the crowds.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book early for the major Monaco events; the rest of the year, last-minute requests are handled subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to the international clientele that frequents Monaco and Cap-d’Ail.",
    },
    {
      q: "Which vehicles do you offer for Cap-d’Ail?",
      a: "A Mercedes sedan, a 7-seat van or an SUV depending on the number of passengers, suited to frequent runs into the Principality.",
    },
  ],
  relatedLinks: [
    { label: "Car service in Monaco", href: "/en/chauffeur/monaco" },
    {
      label: "Private car service at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    { label: "Airport transfers in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Book a chauffeur on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
  ],
  cta: {
    title: "Your private chauffeur in Cap-d’Ail",
    text: "Transfers into Monaco, Mala beach or an airport run: book your chauffeur.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Cap-d’Ail — private chauffeur & transfers",
    description:
      "Private car service in Cap-d’Ail: transfers into Monaco, Mala beach, coastal path and a ~30 min transfer to Nice airport. 5.0/5 from 38 reviews.",
    canonical: "/en/chauffeur/cap-d-ail",
  },
};

export default capDAilEn;
